import React from 'react';
import { Helmet } from 'react-helmet-async';
import { ShieldCheck, FileText, Phone, Mail, Clock, Download, CheckCircle2 } from 'lucide-react';
import { siteConfig } from '../data';
import ContactSection from '../components/ContactSection';

export default function Betingelser() {
  return (
    <div className="bg-slate-50 min-h-screen">
      <Helmet>
        <title>Forretningsbetingelser | Online Tryghed ApS</title>
        <meta 
          name="description" 
          content="Gældende forretningsbetingelser for Online Tryghed ApS. Læs om betalingsvilkår, henvendelser, persondata og ansvarsforhold her." 
        />
      </Helmet>

      {/* Header / Hero */}
      <section className="bg-[#103B66] text-white py-16 lg:py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-white/10 text-blue-200 text-xs font-bold uppercase tracking-wider mb-4">
            <FileText className="w-4 h-4" />
            Vilkår og betingelser
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold mb-4">
            Forretningsbetingelser
          </h1>
          <p className="text-slate-200 text-base sm:text-lg max-w-2xl mx-auto leading-relaxed">
            Her kan du læse de officielle forretningsbetingelser for Online Tryghed ApS. Vi lægger vægt på tryghed, gennemskuelighed og gensidig respekt.
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-12 lg:py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          
          {/* Quick Info Box */}
          <div className="bg-white border border-slate-200 shadow-sm p-6 sm:p-8 mb-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div>
              <h2 className="text-lg font-bold text-[#103B66]">Online Tryghed ApS</h2>
              <p className="text-sm text-slate-600">
                CVR: 43341758 &bull; Fruegade 42B, 4200 Slagelse &bull; Tlf: {siteConfig.phone}
              </p>
            </div>
            <a
              href="/_assets/forretningsbetingelser-for-online-tryghed-aps.pdf"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-semibold transition-colors shrink-0 border border-slate-200"
            >
              <Download className="w-3.5 h-3.5" />
              <span>Hent som PDF</span>
            </a>
          </div>

          {/* Terms Articles */}
          <div className="bg-white border border-slate-200 shadow-sm divide-y divide-slate-100">
            
            {/* 1. Introduktion */}
            <article className="p-6 sm:p-8">
              <div className="flex items-start gap-4">
                <span className="flex items-center justify-center w-8 h-8 rounded-none bg-blue-50 text-blue-700 font-bold text-sm shrink-0">
                  1
                </span>
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-[#103B66] mb-3">Introduktion</h3>
                  <p className="text-slate-600 text-base leading-relaxed">
                    Disse forretningsbetingelser gælder for alle tjenester, produkter og henvendelser via Online Trygheds hjemmeside. Ved at benytte vores hjemmeside og kontakte os, accepterer du disse betingelser.
                  </p>
                </div>
              </div>
            </article>

            {/* 2. Kontakt og henvendelser */}
            <article className="p-6 sm:p-8">
              <div className="flex items-start gap-4">
                <span className="flex items-center justify-center w-8 h-8 rounded-none bg-blue-50 text-blue-700 font-bold text-sm shrink-0">
                  2
                </span>
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-[#103B66] mb-3">Kontakt og henvendelser</h3>
                  <p className="text-slate-600 text-base leading-relaxed">
                    Alle forespørgsler, henvendelser og kommunikation skal ske via de angivne kontaktformer på hjemmesiden. Vi forbeholder os retten til at prioritere og besvare henvendelser efter eget skøn.
                  </p>
                </div>
              </div>
            </article>

            {/* 3. Vedvarende kontakt og uønsket henvendelse */}
            <article className="p-6 sm:p-8">
              <div className="flex items-start gap-4">
                <span className="flex items-center justify-center w-8 h-8 rounded-none bg-blue-50 text-blue-700 font-bold text-sm shrink-0">
                  3
                </span>
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-[#103B66] mb-3">Vedvarende kontakt og uønsket henvendelse</h3>
                  <p className="text-slate-600 text-base leading-relaxed mb-4">
                    Online Tryghed ApS modtager med glæde henvendelser, men gentagen, uhensigtsmæssig kontakt via telefon eller mail kan blive betragtet som spam eller chikane.
                  </p>
                  <ul className="space-y-2 text-slate-600 text-base">
                    <li className="flex items-start gap-2">
                      <span className="text-blue-500 font-bold mt-1">&bull;</span>
                      <span>
                        Hvis du gentagne gange kontakter os uden at få svar, eller uden at modtage en tilfredsstillende respons, forbeholder vi os retten til at ignorere yderligere henvendelser fra samme person eller virksomhed.
                      </span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-blue-500 font-bold mt-1">&bull;</span>
                      <span>
                        Ved vedvarende kontakt, der anses for uønsket eller chikane, kan vi vælge at blokere den pågældende kontakt, og i alvorlige tilfælde, tage juridisk skridt.
                      </span>
                    </li>
                  </ul>
                </div>
              </div>
            </article>

            {/* 4. Ansvarsfraskrivelse */}
            <article className="p-6 sm:p-8">
              <div className="flex items-start gap-4">
                <span className="flex items-center justify-center w-8 h-8 rounded-none bg-blue-50 text-blue-700 font-bold text-sm shrink-0">
                  4
                </span>
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-[#103B66] mb-3">Ansvarsfraskrivelse</h3>
                  <p className="text-slate-600 text-base leading-relaxed">
                    Online Tryghed ApS påtager sig intet ansvar for eventuelle skader, tab eller konsekvenser, der opstår som følge af uønsket eller gentagen kontakt.
                  </p>
                </div>
              </div>
            </article>

            {/* 5. Persondata og fortrolighed */}
            <article className="p-6 sm:p-8">
              <div className="flex items-start gap-4">
                <span className="flex items-center justify-center w-8 h-8 rounded-none bg-blue-50 text-blue-700 font-bold text-sm shrink-0">
                  5
                </span>
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-[#103B66] mb-3">Persondata og fortrolighed</h3>
                  <p className="text-slate-600 text-base leading-relaxed mb-4">
                    Alle personoplysninger, der indsamles via hjemmeside eller kontakt, håndteres i overensstemmelse med gældende databeskyttelseslovgivning.
                  </p>
                  <p className="text-slate-600 text-base leading-relaxed">
                    Ved uønsket kontakt kan vi opbevare oplysninger om dette for at dokumentere, at vi har gjort vores bedste for at håndtere henvendelsen korrekt.
                  </p>
                </div>
              </div>
            </article>

            {/* 6. Ændringer af betingelser */}
            <article className="p-6 sm:p-8">
              <div className="flex items-start gap-4">
                <span className="flex items-center justify-center w-8 h-8 rounded-none bg-blue-50 text-blue-700 font-bold text-sm shrink-0">
                  6
                </span>
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-[#103B66] mb-3">Ændringer af betingelser</h3>
                  <p className="text-slate-600 text-base leading-relaxed">
                    Online Tryghed ApS forbeholder sig retten til at opdatere eller ændre disse forretningsbetingelser uden varsel. Det er brugerens ansvar at holde sig orienteret om de gældende vilkår.
                  </p>
                </div>
              </div>
            </article>

            {/* 7. Betaling */}
            <article className="p-6 sm:p-8">
              <div className="flex items-start gap-4">
                <span className="flex items-center justify-center w-8 h-8 rounded-none bg-blue-50 text-blue-700 font-bold text-sm shrink-0">
                  7
                </span>
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-[#103B66] mb-3">Betaling</h3>
                  <p className="text-slate-600 text-base leading-relaxed">
                    Når du som kunde kontakter Online Tryghed ApS, bliver du bedt om at acceptere at betale for den ydelse du modtager. Vi dokumenterer din henvendelse via vores telefonsystem og mailserver. Vi forbeholder os retten til at skal have betaling før ydelsen er leveret, hvis du f.eks. har udeladt telefonnummer, hemmelig adresse, mv.
                  </p>
                </div>
              </div>
            </article>

            {/* 8. Kontakt */}
            <article className="p-6 sm:p-8">
              <div className="flex items-start gap-4">
                <span className="flex items-center justify-center w-8 h-8 rounded-none bg-blue-50 text-blue-700 font-bold text-sm shrink-0">
                  8
                </span>
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-[#103B66] mb-3">Kontakt</h3>
                  <p className="text-slate-600 text-base leading-relaxed mb-4">
                    Hvis du har spørgsmål til disse betingelser eller ønsker at anmode om at blive fjernet fra vores kontaktlister, kan du altid kontakte os:
                  </p>
                  <div className="bg-slate-50 p-4 border border-slate-200 text-sm space-y-1">
                    <p className="font-semibold text-slate-800">Online Tryghed ApS</p>
                    <p className="text-slate-600">Email: <a href="mailto:backoffice@onlinetryghed.dk" className="text-blue-600 hover:underline">backoffice@onlinetryghed.dk</a></p>
                    <p className="text-slate-600">Telefon: <a href={`tel:${siteConfig.phone.replace(/\s/g, '')}`} className="text-blue-600 hover:underline">{siteConfig.phone}</a></p>
                  </div>
                </div>
              </div>
            </article>

          </div>

        </div>
      </section>

      <ContactSection />
    </div>
  );
}
